import { useCallback, useEffect, useRef, useState } from "react";
import { Room } from "livekit-client";
import { useToast } from "@/components/ui/use-toast";
import { debounce } from "@/utils/livekitCloudUtils";
import {
  checkForMultipleMonitors,
  monitorDetector,
  requestMonitorPermissions,
  startMonitorMonitoring,
  type MonitorDetectionResult,
} from "@/lib/monitorDetection";
import { 
  MonitoringConfig, 
  MonitoringState, 
  PermissionStatus,
  MonitorDetectionAction,
  ViolationType
} from "@/types/video-call";
import { DEFAULT_PERMISSION_STATUS, VIDEO_CALL_END_REASONS } from "@/constants/video-call";
import { TimelineEventService } from "@/services/video-call/timelineEventService";
import { CallService } from "@/services/video-call/callService";
import { AudioPlayer } from "@/utils/audio";
import * as Sentry from "@sentry/react";

interface UseMonitoringProps {
  config: MonitoringConfig;
  room: Room | null;
  callActive: boolean;
  strictMonitorMode: boolean;
  callId?: string;
  organizationId?: string;
  callType?: 'voice_call' | 'video_interview';
  onPauseCall?: (reason: "monitor_detected") => void;
  onResumeCall?: () => void;
  onShowTabChangePenalty?: () => void;
  onShowWindowBlurPenalty?: () => void;
  onAddViolation?: (type: ViolationType, message: string, severity: "warning" | "penalty" | "critical", duration?: number) => void;
  onEndCall?: (reason: string) => void;
  isIntentionalEnding?: boolean;
}

export const useMonitoring = ({
  config,
  room,
  callActive,
  strictMonitorMode,
  callId,
  organizationId,
  callType = 'video_interview',
  onPauseCall,
  onResumeCall,
  onShowTabChangePenalty,
  onShowWindowBlurPenalty,
  onAddViolation,
  onEndCall,
  isIntentionalEnding = false
}: UseMonitoringProps) => {
  const { toast } = useToast();

  // Violation tracking
  const [violationCounts, setViolationCounts] = useState({
    tabChange: 0,
    windowBlur: 0,
    fullscreen: 0,
  });
  const [showViolationLimitWarning, setShowViolationLimitWarning] = useState(false);
  const VIOLATION_LIMIT = 3;

  // Monitoring state
  const [monitoringState, setMonitoringState] = useState<MonitoringState>({
    currentMonitorResult: null,
    monitorViolationDetected: false,
    monitorPermissionRequested: false,
    currentScreenCount: 1,
    previousScreenCount: 1,
    permissionStatus: {
      ...DEFAULT_PERMISSION_STATUS,
      screenShare: config.screenSharingEnabled ? "pending" : "granted",
      monitorCheck: config.externalMonitorDetection ? "pending" : "passed",
    },
  });

  // Flag to skip first detection after reset to prevent false positives
  const skipNextViolationRef = useRef(false);
  
  // Flag to track when a tab change is in progress to suppress window blur events  
    const suppressEventsRef = useRef({ blur: false, focus: false });

  // Processing flag to prevent duplicate monitor detection handling
  const isProcessingMonitorDetectionRef = useRef(false);

  // Flag to track if we've already detected a monitor violation for this call
  const monitorViolationAlreadyDetectedRef = useRef(false);

  // Flag to suppress window blur events when monitor changes are happening
  const monitorChangeInProgressRef = useRef(false);

  // UI state for dialogs
  const [showMonitorWarning, setShowMonitorWarning] = useState(false);
  const [showMonitorBlockDialog, setShowMonitorBlockDialog] = useState(false);
  const [showExternalMonitorWarning, setShowExternalMonitorWarning] = useState(false);

  // Tab change tracking
  const [tabChangeData, setTabChangeData] = useState({
    count: 0,
    changes: [] as Array<{
      timestamp: Date;
      type: "hidden" | "visible";
      duration?: number;
      windowTitle: string;
      url: string;
      userAgent: string;
      screenResolution: string;
      timezone: string;
    }>,
    lastVisibleTime: null as Date | null,
  });

  // Validate screen share scope
  const validateScreenShareScope = useCallback(async (
    stream: MediaStream
  ): Promise<{
    isValid: boolean;
    detectedScope: string;
    confidence: number;
  }> => {
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) {
      return { isValid: false, detectedScope: "unknown", confidence: 0 };
    }

    const settings = videoTrack.getSettings();

    // Method 1: Direct API detection using displaySurface
    if (settings.displaySurface) {
      const isValid = settings.displaySurface === "monitor";
      return {
        isValid,
        detectedScope: settings.displaySurface,
        confidence: 1.0,
      };
    }

    // Method 2: Resolution analysis fallback
    const { width = 0, height = 0 } = settings;
    const aspectRatio = width / height;
    const pixels = width * height;

    // Common monitor resolutions that suggest full screen sharing
    const monitorSignatures = [
      { w: 1920, h: 1080 }, { w: 2560, h: 1440 }, { w: 3840, h: 2160 },
      { w: 1366, h: 768 }, { w: 1280, h: 720 }, { w: 1440, h: 900 },
      { w: 2048, h: 1152 }, { w: 3440, h: 1440 }, { w: 5120, h: 2880 },
    ];

    const tolerance = 50;
    const isMonitorResolution = monitorSignatures.some(
      (sig) =>
        Math.abs(width - sig.w) <= tolerance &&
        Math.abs(height - sig.h) <= tolerance
    );

    const isHighResolution = pixels > 1000000;
    const isStandardAspectRatio =
      Math.abs(aspectRatio - 16 / 9) < 0.1 ||
      Math.abs(aspectRatio - 16 / 10) < 0.1 ||
      Math.abs(aspectRatio - 4 / 3) < 0.1;

    if (isMonitorResolution && isHighResolution) {
      return { isValid: true, detectedScope: "monitor", confidence: 0.9 };
    } else if (isHighResolution && isStandardAspectRatio) {
      return { isValid: true, detectedScope: "monitor", confidence: 0.7 };
    } else if (pixels < 500000 || aspectRatio > 2.5 || aspectRatio < 0.5) {
      return {
        isValid: false,
        detectedScope: "window_or_tab",
        confidence: 0.8,
      };
    }

    return { isValid: true, detectedScope: "uncertain", confidence: 0.3 };
  }, []);

  const sendDataToRoom = useCallback((data: any) => {
    if (room && room.state === "connected") {
      const encoder = new TextEncoder();
      const encodedData = encoder.encode(JSON.stringify(data));
      room.localParticipant.publishData(encodedData, { reliable: true });
    }
  }, [room]);

  // Create a stable monitor detection handler that always uses current state
  const handleMonitorDetection = useCallback((result: MonitorDetectionResult) => {
    // Prevent duplicate processing
    if (isProcessingMonitorDetectionRef.current) {
      return;
    }

    // Set processing flag
    isProcessingMonitorDetectionRef.current = true;

    // Monitor detection handler triggered

    // Get current state directly from the latest monitoringState
    setMonitoringState(prev => {
      const previousScreenCount = prev.previousScreenCount || 1;
      const currentScreenCount = result.screenCount;
      
      // Only proceed if this is actually a change in screen count or first detection
      const isActualChange = !prev.currentMonitorResult || currentScreenCount !== previousScreenCount;
      const isNewViolation = currentScreenCount > previousScreenCount;
      
      // console.log("🔍 Monitor detection handler:", {
      //   previousScreenCount,
      //   currentScreenCount,
      //   isActualChange,
      //   isNewViolation,
      //   hasMultipleMonitors: result.hasMultipleMonitors,
      //   hadPreviousResult: !!prev.currentMonitorResult,
      //   skipNextViolation: skipNextViolationRef.current
      // });

      // Clear the skip flag after first detection
      if (skipNextViolationRef.current) {
        skipNextViolationRef.current = false;
      }
      
      // Update state first
      const newState = {
        ...prev,
        currentMonitorResult: result,
        previousScreenCount: prev.currentScreenCount,
        currentScreenCount: currentScreenCount,
        permissionStatus: {
          ...prev.permissionStatus,
          monitorCheck: result.hasMultipleMonitors ? "failed" : ("passed" as "passed" | "failed" | "pending"),
        },
      };

      // Handle violations only on actual changes and if we had a previous result
      // Also skip if this is the first detection after reset
      if (result.hasMultipleMonitors && isActualChange && prev.currentMonitorResult && !skipNextViolationRef.current) {
        // Set flag to suppress window blur events caused by monitor changes
        monitorChangeInProgressRef.current = true;
        
        // Clear the flag after monitor change operations complete
        setTimeout(() => {
          monitorChangeInProgressRef.current = false;
        }, 3000);
        
        // Set violation detected flag
        newState.monitorViolationDetected = true;

        const violationMessage = isNewViolation 
          ? `External monitor connected during interview: ${currentScreenCount} displays (was ${previousScreenCount}), Ending call`
          : `Multiple monitors detected at start: ${result.screenCount} displays`;
        
        // Add violation to the violations list immediately when detected
        if (onAddViolation) {
          onAddViolation(
            "multiple_monitors",
            violationMessage,
            isNewViolation ? "critical" : "warning"
          );
        }

        // For any monitor violation - immediate termination (security requirement)
        AudioPlayer.playCriticalSound();
        
        // Show external monitor warning
        setShowExternalMonitorWarning(true);
        
        // Send immediate termination signal to agent
        sendDataToRoom({
          type: "violation_termination",
          violation_type: "multiple_monitors",
          action: "terminate_immediately",
          reason: isNewViolation 
            ? "External monitor connected during interview" 
            : "Multiple monitors detected at start",
          screenCount: currentScreenCount,
          previousScreenCount,
          timestamp: new Date().toISOString()
        });
        
        // Also terminate via backend API to stop agent
        if (callId) {
          CallService.terminateForViolation(
            callId,
            organizationId,
            "multiple_monitors",
            isNewViolation 
              ? "External monitor connected during interview"
              : "Multiple monitors detected at start",
            { screenCount: currentScreenCount, previousScreenCount }
          ).catch(error => {
            Sentry.logger.error("Failed to terminate call via backend", { error, component: 'useMonitoring', callId });
          });
        }
        
        // End the call immediately - don't wait for warnings or delays
        if (onEndCall) {
          onEndCall(VIDEO_CALL_END_REASONS.MONITOR_VIOLATION);
        } else {
          Sentry.logger.error("onEndCall callback is not available!", { component: 'useMonitoring', callId });
        }
      }

      // Send detection data to backend only on actual changes
      if (isActualChange) {
        sendDataToRoom({
          type: "monitor_detection",
          result,
          violation: result.hasMultipleMonitors,
          previousScreenCount,
          currentScreenCount,
          isNewViolation,
        });

        // Store event in database only on actual changes and only once per call
        if (callId && !monitorViolationAlreadyDetectedRef.current) {
          // Mark that we've stored a monitor violation for this call
          monitorViolationAlreadyDetectedRef.current = true;
          
          const timelineEvent = TimelineEventService.createMonitorDetectionEvent(
            result.hasMultipleMonitors,
            result.screenCount,
            previousScreenCount,
            result.detectionMethod || 'unknown',
            result.confidence || 0,
            isNewViolation,
            result
          );
          
          TimelineEventService.addTimelineEvent(callId, timelineEvent).catch(error => {
            Sentry.logger.error('Monitor detection event storage exception', { error, component: 'useMonitoring', callId });
          });
        } else if (callId && monitorViolationAlreadyDetectedRef.current) {
          // Skipping duplicate monitor violation event storage
        }
      }

      return newState;
    });

    // Reset processing flag after a delay to prevent race conditions
    setTimeout(() => {
      isProcessingMonitorDetectionRef.current = false;
    }, 3000); // 3 second delay (much shorter than 30-second detection interval)
  }, [sendDataToRoom, callId, organizationId, onAddViolation, onEndCall]);

  const requestMonitorDetectionPermissions = useCallback(async () => {
    if (!config.externalMonitorDetection || monitoringState.monitorPermissionRequested) return;

    setMonitoringState(prev => ({ ...prev, monitorPermissionRequested: true }));
    try {
      const permissionGranted = await requestMonitorPermissions();
      // Window Management API permission result is not logged to console
    } catch (error) {
      Sentry.logger.error("Error requesting monitor permissions", { error, component: 'useMonitoring' });
    }
  }, [config.externalMonitorDetection, monitoringState.monitorPermissionRequested]);

  // Check if permissions are already granted without requesting them
  const checkExistingPermissions = useCallback(async () => {
    try {
      // Check camera and microphone permissions using permissions API
      const cameraPermission = await navigator.permissions.query({ name: 'camera' as PermissionName });
      const microphonePermission = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      
      // Existing permissions checked via Permissions API

      // If both are granted, update state without requesting
      if (cameraPermission.state === 'granted' && microphonePermission.state === 'granted') {
        setMonitoringState(prev => ({
          ...prev,
          permissionStatus: {
            ...prev.permissionStatus,
            camera: "granted",
            microphone: "granted",
          },
        }));
        return true;
      }
      
      // If either is denied, mark as denied
      if (cameraPermission.state === 'denied' || microphonePermission.state === 'denied') {
        setMonitoringState(prev => ({
          ...prev,
          permissionStatus: {
            ...prev.permissionStatus,
            camera: cameraPermission.state === 'denied' ? "denied" : prev.permissionStatus.camera,
            microphone: microphonePermission.state === 'denied' ? "denied" : prev.permissionStatus.microphone,
          },
        }));
        return false;
      }
      
      // If permissions are 'prompt', leave as pending
      return false;
    } catch (error) {
      Sentry.logger.error("Error checking existing permissions", { error, component: 'useMonitoring' });
      return false;
    }
  }, []);

  const requestPermissions = useCallback(async () => {
    try {
      // Check for multiple monitors first if enabled
      if (config.externalMonitorDetection) {
        const monitorResult = await checkForMultipleMonitors();
        const shouldBlock = monitorDetector.shouldBlockAccess(monitorResult, strictMonitorMode);

        if (shouldBlock) {
          handleMonitorDetection(monitorResult);
          return false;
        } else {
          setMonitoringState(prev => ({
            ...prev,
            permissionStatus: {
              ...prev.permissionStatus,
              monitorCheck: "passed",
            },
            currentMonitorResult: monitorResult,
          }));
        }
      }

      // Request camera and microphone permissions
      try {
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });

        setMonitoringState(prev => ({
          ...prev,
          permissionStatus: {
            ...prev.permissionStatus,
            camera: "granted",
            microphone: "granted",
          },
        }));

        // Keep the stream active but stop it after a delay to ensure it doesn't interfere
        // This prevents black screen issues where permissions are granted but camera isn't ready
        setTimeout(() => {
          mediaStream.getTracks().forEach(track => track.stop());
        }, 2000);
      } catch (mediaError) {
        Sentry.logger.error("Media permissions denied", { error: mediaError, component: 'useMonitoring' });
        setMonitoringState(prev => ({
          ...prev,
          permissionStatus: {
            ...prev.permissionStatus,
            camera: "denied",
            microphone: "denied",
          },
        }));
        return false;
      }

      // Request screen share permission if enabled
      if (config.screenSharingEnabled) {
        // Check if we already have a valid screen share stream or permission
        if (monitoringState.permissionStatus.screenShare === "granted" && 
            monitoringState.validatedScreenStream && 
            monitoringState.validatedScreenStream.active) {
          // Permission already granted and stream is active, no need to request again
        } else {
          try {
            const displayMediaOptions = {
              video: {
                displaySurface: "monitor",
                cursor: "always",
                width: { ideal: 1920, max: 1920 },
                height: { ideal: 1080, max: 1080 },
                frameRate: { ideal: 30, max: 30 },
              } as MediaTrackConstraints,
              audio: false,
              preferCurrentTab: false,
              selfBrowserSurface: "exclude",
            };

            // Requesting new screen share permission
            const screenStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);

            // Validate screen sharing scope before proceeding
            const validation = await validateScreenShareScope(screenStream);

            if (!validation.isValid) {
              // Stop the invalid stream
              screenStream.getTracks().forEach(track => track.stop());

              let errorMessage = "Invalid screen sharing detected. ";
              if (validation.detectedScope === "window_or_tab") {
                errorMessage += "Please share your entire screen, not just a window or tab.";
              } else {
                errorMessage += "For proctoring compliance, only entire screen sharing is allowed.";
              }

              AudioPlayer.playWarningSound();
              toast({
                title: "Screen Sharing Validation Failed",
                description: errorMessage,
                variant: "destructive",
              });

              setMonitoringState(prev => ({
                ...prev,
                permissionStatus: {
                  ...prev.permissionStatus,
                  screenShare: "denied",
                },
              }));
              return false;
            }

            // Validation passed - store the validated stream
            setMonitoringState(prev => ({
              ...prev,
              permissionStatus: {
                ...prev.permissionStatus,
                screenShare: "granted",
              },
              validatedScreenStream: screenStream,
            }));

            // CRITICAL: Keep the stream active by creating a hidden video element
            // This prevents browser from terminating the stream due to inactivity
            const hiddenVideo = document.createElement('video');
            hiddenVideo.style.display = 'none';
            hiddenVideo.style.position = 'absolute';
            hiddenVideo.style.top = '-9999px';
            hiddenVideo.muted = true;
            hiddenVideo.autoplay = true;
            hiddenVideo.srcObject = screenStream;
            document.body.appendChild(hiddenVideo);
            

            // Clean up hidden video when stream ends
            const cleanupHiddenVideo = () => {
              if (hiddenVideo.parentNode) {
                document.body.removeChild(hiddenVideo);
              }
            };

            // Store cleanup function for later use
            (screenStream as any)._hiddenVideoCleanup = cleanupHiddenVideo;

            // Listen for stream ending to clear the validated stream
            const videoTrack = screenStream.getVideoTracks()[0];
            
            // Add multiple event listeners to detect stream issues early
            videoTrack.addEventListener("ended", () => {
              setMonitoringState(prev => ({
                ...prev,
                validatedScreenStream: undefined,
                // Only set to "denied" if we're not in an active call
                // During active calls, screen share ending should be handled by useMediaControls
                permissionStatus: {
                  ...prev.permissionStatus,
                  screenShare: room ? "granted" : "denied",
                },
              }));
            });
            
            // Monitor for mute/unmute events that might indicate stream issues
            videoTrack.addEventListener("mute", () => {
            });
            
            videoTrack.addEventListener("unmute", () => {
            });
            
            // Monitor stream inactive events
            screenStream.addEventListener("inactive", () => {
            });
          } catch (screenError) {
            Sentry.logger.error("Screen share permission denied", { error: screenError, component: 'useMonitoring' });
            setMonitoringState(prev => ({
              ...prev,
              permissionStatus: {
                ...prev.permissionStatus,
                screenShare: "denied",
              },
            }));
            return false;
          }
        }
      }

      return true;
    } catch (error) {
      Sentry.logger.error("Error requesting permissions", { error, component: 'useMonitoring' });
      return false;
    }
  }, [config.externalMonitorDetection, config.screenSharingEnabled, strictMonitorMode, handleMonitorDetection, monitoringState.permissionStatus.screenShare, monitoringState.validatedScreenStream, validateScreenShareScope, toast]);

  // Tab change detection effect
  useEffect(() => {
    if (!config.tabChangeDetection || !room) {
      return;
    }


    const handleVisibilityChange = () => {
      const timestamp = new Date();
      const type: "hidden" | "visible" = document.hidden ? "hidden" : "visible";
      let duration: number | undefined;


      if (document.hidden) {
        // When tab is hidden, suppress the upcoming blur event
        suppressEventsRef.current.blur = true;
        // Set a timeout to reset the suppression flag for blur, in case blur event doesn't fire for some reason
        setTimeout(() => {
          suppressEventsRef.current.blur = false;
        }, 500); // Reset after 500ms
      } else {
        // When tab becomes visible, suppress the upcoming focus event
        suppressEventsRef.current.focus = true;
      }

      // Calculate duration if transitioning from hidden to visible
      if (!document.hidden && tabChangeData.lastVisibleTime) {
        duration = timestamp.getTime() - tabChangeData.lastVisibleTime.getTime();
      }

      const changeDetail = {
        timestamp,
        type,
        duration,
        windowTitle: document.title,
        url: window.location.href,
        userAgent: navigator.userAgent,
        screenResolution: `${window.screen.width}x${window.screen.height}`,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      setTabChangeData(prev => ({
        ...prev,
        changes: [...prev.changes, changeDetail],
        lastVisibleTime: !document.hidden ? timestamp : prev.lastVisibleTime,
        count: document.hidden ? prev.count + 1 : prev.count,
      }));

      if (document.hidden) {
        // Play warning sound immediately when tab becomes hidden
        AudioPlayer.playWarningSound();
        
        sendDataToRoom({
          type: "tab_change",
          action: "hidden",
          count: tabChangeData.count + 1,
          timestamp: timestamp.toISOString(),
          details: changeDetail,
        });

        // Store tab change event in database
        if (callId) {
          const timelineEvent = TimelineEventService.createTabChangeEvent(
            'hidden',
            duration,
            { count: tabChangeData.count + 1 }
          );
          
          TimelineEventService.addTimelineEvent(callId, timelineEvent)
            .then(() => {
              // Tab change event stored successfully
            })
            .catch(error => {
              Sentry.logger.error('Tab change event storage exception', { error, component: 'useMonitoring', callId });
            });
        }

        // Process violation immediately when tab becomes hidden
        // Tab change violation detected (hidden)

        // Increment violation count immediately when tab becomes hidden
        setViolationCounts(prev => {
          const newTabChangeCount = prev.tabChange + 1;
          const totalViolations = newTabChangeCount + prev.windowBlur;
          
          // Tab change violation count updated (hidden)
          
          // Track violations but do not terminate call - no additional audio since warning sound already played
          if (totalViolations >= VIOLATION_LIMIT) {
            Sentry.logger.warn("Tab change violation limit reached but call continues", {
              component: 'useMonitoring',
              callId,
              totalViolations,
              newTabChangeCount,
              windowBlurCount: prev.windowBlur,
              fullscreenCount: prev.fullscreen,
              violationLimit: VIOLATION_LIMIT
            });
          }
          // Don't show penalty overlay on hidden - user can't see it
          // Penalty overlay will be shown when they return (visible action)
          
          return { ...prev, tabChange: newTabChangeCount };
        });
        
        // Add violation immediately when detected
        onAddViolation?.("tab_change", "Tab changed during proctored session", "penalty", 5);
      } else {
        // Tab became visible - show penalty overlay if there were violations
        // Check if there were any previous violations that user hasn't seen
        const hasUnseenViolations = tabChangeData.changes.length > 0 || (tabChangeData.lastVisibleTime && duration > 0);
        if (hasUnseenViolations) {
          Sentry.logger.info("Showing penalty overlay for previous tab change", {
            component: 'useMonitoring',
            callId,
            timestamp: new Date().toISOString(),
            violationType: "tab_change",
            previousChanges: tabChangeData.changes.length,
            duration: duration || 0,
            showingPenaltyOverlay: true
          });
          
          // Show penalty overlay when user returns
          onShowTabChangePenalty?.();
        }
        
        sendDataToRoom({
          type: "tab_change",
          action: "visible",
          count: tabChangeData.count,
          timestamp: timestamp.toISOString(),
          details: changeDetail,
        });

        // Store tab change event in database
        if (callId) {
          const timelineEvent = TimelineEventService.createTabChangeEvent(
            'visible',
            duration,
            { count: tabChangeData.count }
          );
          
          TimelineEventService.addTimelineEvent(callId, timelineEvent).catch(error => {
            console.error('Tab change event storage exception:', error);
          });
        }
      }
    };

    // Enhanced detection with multiple event listeners
    const handleFocusOut = () => {

      if (suppressEventsRef.current.blur) {
        suppressEventsRef.current.blur = false; // Reset flag
        return;
      }
      
      // Use setTimeout to check visibility state shortly after blur
      setTimeout(() => {
        if (document.hidden) {
          return;
        }

        if (monitorChangeInProgressRef.current) {
          return;
        }
        
        // Store window blur event in database
        if (callId) {
          const timelineEvent = TimelineEventService.createWindowBlurEvent(
            'blur',
            { 
              documentHidden: document.hidden,
              visibilityState: document.visibilityState 
            }
          );
          
          TimelineEventService.addTimelineEvent(callId, timelineEvent)
            .then(() => {
              // Window blur event stored successfully
            })
            .catch(error => {
              Sentry.logger.error('Window blur event storage exception', { error, component: 'useMonitoring', callId });
            });
        }

        // Always show window blur penalty when window loses focus during monitored session
        // This includes minimizing the window, which should be considered a violation
        if (room) {
          Sentry.logger.info("Window blur violation detected", {
            component: 'useMonitoring',
            callId,
            timestamp: new Date().toISOString(),
            violationType: "window_blur",
            documentHidden: document.hidden,
            visibilityState: document.visibilityState,
            callActive,
            roomState: room?.state,
            violationActive: true
          });

          // Increment violation count
          setViolationCounts(prev => {
            const newWindowBlurCount = prev.windowBlur + 1;
            const totalViolations = prev.tabChange + newWindowBlurCount;
            
            Sentry.logger.info("Window blur violation count update", {
              component: 'useMonitoring',
              callId,
              previousWindowBlurCount: prev.windowBlur,
              newWindowBlurCount,
              tabChangeCount: prev.tabChange,
              totalViolations,
              violationLimit: VIOLATION_LIMIT,
              willTerminate: totalViolations >= VIOLATION_LIMIT,
              timestamp: new Date().toISOString()
            });
            
            // Track violations but do not terminate call
            if (totalViolations >= VIOLATION_LIMIT) {
              AudioPlayer.playViolationSound();
              Sentry.logger.warn("Window blur violation limit reached but call continues", {
                component: 'useMonitoring',
                callId,
                totalViolations,
                newWindowBlurCount,
                tabChangeCount: prev.tabChange,
                fullscreenCount: prev.fullscreen,
                violationLimit: VIOLATION_LIMIT
              });
            } else {
              // Play violation sound for window blur violation
              AudioPlayer.playViolationSound();
            }
            
            // Always show penalty overlay
            onShowWindowBlurPenalty?.();
            
            return { ...prev, windowBlur: newWindowBlurCount };
          });

          // Add violation with appropriate message based on type
          const violationMessage = document.hidden 
            ? "Window minimized during proctored session" 
            : "Window focus lost during proctored session";
          
          onAddViolation?.("window_blur", violationMessage, "penalty", 3);
          
          // Send data to room
          sendDataToRoom({
            type: "window_blur",
            action: "blur",
            timestamp: new Date().toISOString(),
            details: {
              documentHidden: document.hidden,
              visibilityState: document.visibilityState,
              violationType: document.hidden ? "window_minimized" : "focus_lost"
            }
          });
          
          setTimeout(() => {
            if (document.hidden) {
              // Confirmed tab change via delayed check after blur
            }
          }, 100);
        }
      }, 100); // 100ms delay to allow visibility change event to fire first
    };

    const handleFocusIn = () => {

      if (suppressEventsRef.current.focus) {
        suppressEventsRef.current.focus = false; // Reset flag
        return;
      }
      
      // Store window focus event in database
      if (callId) {
        const timelineEvent = TimelineEventService.createWindowBlurEvent(
          'focus',
          { 
            documentHidden: document.hidden,
            visibilityState: document.visibilityState 
          }
        );
        
        TimelineEventService.addTimelineEvent(callId, timelineEvent).catch(error => {
          Sentry.logger.error('Window focus event storage exception', { error, component: 'useMonitoring', callId });
        });
      }
    };

    const handlePageHide = () => {
      // Page hide event triggered
    };

    const handlePageShow = () => {
      // Page show event triggered
    };

    // Track window state for maximize/restore detection
    let previousWindowState = {
      outerWidth: window.outerWidth,
      outerHeight: window.outerHeight,
      screenX: window.screenX || window.screenLeft || 0,
      screenY: window.screenY || window.screenTop || 0,
      isMaximized: window.outerWidth >= (screen.availWidth - 10) && window.outerHeight >= (screen.availHeight - 10)
    };

    const handleWindowResize = async () => {
      if (!callActive) return;

      const currentWindowState = {
        outerWidth: window.outerWidth,
        outerHeight: window.outerHeight,
        screenX: window.screenX || window.screenLeft || 0,
        screenY: window.screenY || window.screenTop || 0,
        isMaximized: window.outerWidth >= (screen.availWidth - 10) && window.outerHeight >= (screen.availHeight - 10)
      };

      // Detect significant window state changes (maximize/restore operations)
      const sizeChanged = Math.abs(currentWindowState.outerWidth - previousWindowState.outerWidth) > 50 ||
                         Math.abs(currentWindowState.outerHeight - previousWindowState.outerHeight) > 50;
      
      const positionChanged = Math.abs(currentWindowState.screenX - previousWindowState.screenX) > 10 ||
                             Math.abs(currentWindowState.screenY - previousWindowState.screenY) > 10;
      
      const maximizeStateChanged = currentWindowState.isMaximized !== previousWindowState.isMaximized;

      // Function to apply window manipulation penalty
      const applyWindowManipulationPenalty = () => {
        // Store window resize event in database
        if (callId) {
          const action = maximizeStateChanged ? (currentWindowState.isMaximized ? 'maximize' : 'restore') : 'resize';
          const timelineEvent = TimelineEventService.createWindowManipulationEvent(
            action,
            previousWindowState,
            currentWindowState,
            {
              sizeChanged,
              positionChanged,
              maximizeStateChanged
            }
          );
          
          TimelineEventService.addTimelineEvent(callId, timelineEvent).catch(error => {
            Sentry.logger.error('Window manipulation event storage exception', { error, component: 'useMonitoring', callId });
          });
        }

        // Increment violation count
        setViolationCounts(prev => {
          const newCount = prev.windowBlur + 1;
          
          // Track violations but do not terminate call
          if (newCount >= VIOLATION_LIMIT) {
            AudioPlayer.playViolationSound();
            Sentry.logger.warn("Window manipulation violation limit reached but call continues", {
              component: 'useMonitoring',
              callId,
              count: newCount,
              violationLimit: VIOLATION_LIMIT
            });
          } else {
            // Play violation sound for window manipulation
            AudioPlayer.playViolationSound();
          }
          
          // Always show penalty overlay
          onShowWindowBlurPenalty?.();
          
          return { ...prev, windowBlur: newCount };
        });

        // Determine violation message based on the type of change
        let violationMessage = "Window manipulated during proctored session";
        if (maximizeStateChanged) {
          violationMessage = currentWindowState.isMaximized 
            ? "Window maximized during proctored session"
            : "Window restored/unmaximized during proctored session";
        } else if (sizeChanged) {
          violationMessage = "Window resized during proctored session";
        } else if (positionChanged) {
          violationMessage = "Window moved during proctored session";
        }

        // Add violation
        onAddViolation?.("window_blur", violationMessage, "penalty", 3);
        
        // Send data to room
        sendDataToRoom({
          type: "window_manipulation",
          action: maximizeStateChanged ? (currentWindowState.isMaximized ? 'maximize' : 'restore') : 'resize',
          timestamp: new Date().toISOString(),
          details: {
            previousState: previousWindowState,
            currentState: currentWindowState,
            sizeChanged,
            positionChanged,
            maximizeStateChanged,
            violationType: maximizeStateChanged ? 'maximize_restore' : (sizeChanged ? 'resize' : 'move')
          }
        });

        // Update previous state
        previousWindowState = currentWindowState;
      };

      if (sizeChanged || positionChanged || maximizeStateChanged) {
        // IMPORTANT: Check for external monitor first before treating as window manipulation
        if (config.externalMonitorDetection) {
          // Delay window manipulation penalty to give monitor detection time to complete
          setTimeout(async () => {
            try {
              // Try multiple times with small delays to catch monitor changes
              let monitorResult = await checkForMultipleMonitors();
              
              // If first check doesn't detect new monitor, try again with slight delay
              if (!monitorResult.hasMultipleMonitors || monitorResult.screenCount <= (monitoringState.currentMonitorResult?.screenCount || 1)) {
                await new Promise(resolve => setTimeout(resolve, 100));
                monitorResult = await checkForMultipleMonitors();
              }
              
              const currentScreenCount = monitorResult.screenCount;
              const previousScreenCount = monitoringState.currentMonitorResult?.screenCount || 1;
              
              // If external monitor was detected, handle as monitor violation instead
              if (monitorResult.hasMultipleMonitors && currentScreenCount > previousScreenCount) {
                handleMonitorDetection(monitorResult);
                // Update previous state and return early to avoid window manipulation penalty
                previousWindowState = currentWindowState;
                return;
              }
              
              // If no external monitor detected, proceed with window manipulation penalty
              applyWindowManipulationPenalty();
              
            } catch (error) {
              Sentry.logger.error("Error checking monitors during window resize", { error, component: 'useMonitoring', callId });
              // If monitor check fails, apply window manipulation penalty as fallback
              applyWindowManipulationPenalty();
            }
          }, 50); // 50ms delay to let browser update screen info
          
          // Don't apply window manipulation penalty immediately - let the delayed check handle it
          previousWindowState = currentWindowState;
          return;
        }

        // If external monitor detection is disabled, apply penalty immediately
        applyWindowManipulationPenalty();
      }
    };

    // Add event listeners
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleFocusOut);
    window.addEventListener("focus", handleFocusIn);
    window.addEventListener("pagehide", handlePageHide);
    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("resize", handleWindowResize);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleFocusOut);
      window.removeEventListener("focus", handleFocusIn);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("resize", handleWindowResize);

      // Send summary when component unmounts
      if (tabChangeData.changes.length > 0) {
        sendDataToRoom({
          type: "tab_change_summary",
          total_changes: tabChangeData.count,
          changes: tabChangeData.changes,
          session_end: new Date().toISOString(),
        });
      }
    };
  }, [config.tabChangeDetection, room, tabChangeData.count, tabChangeData.lastVisibleTime, sendDataToRoom, callId, callType, onShowTabChangePenalty, onShowWindowBlurPenalty, onAddViolation, onEndCall]);

  // Beforeunload/unload protection effect
  useEffect(() => {
    if (!callActive) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // Skip if this is an intentional ending (user clicked End Interview)
      if (isIntentionalEnding) {
        // Skipping beforeunload - intentional ending
        return;
      }

      // Send data to room if possible
      sendDataToRoom({
        type: "beforeunload_attempt",
        timestamp: new Date().toISOString(),
        callActive: callActive,
      });

      // Store event in database
      if (callId) {
        const timelineEvent = TimelineEventService.createUnloadEvent(
          'beforeunload_attempt',
          callActive,
          {
            timestamp: new Date().toISOString()
          }
        );
        
        TimelineEventService.addTimelineEvent(callId, timelineEvent).catch(error => {
          Sentry.logger.error('Beforeunload event storage exception', { error, component: 'useMonitoring', callId });
        });
      }

      // Show confirmation dialog
      const message = "You are about to leave an active proctored session. This may be recorded as a violation. Are you sure?";
      event.preventDefault();
      event.returnValue = message;
      return message;
    };

    const handleUnload = () => {
      // Send final data to room (might not reach if connection is closing)
      sendDataToRoom({
        type: "page_unload",
        timestamp: new Date().toISOString(),
        callActive: callActive,
      });

      // Store event in database (might not reach if connection is closing)
      if (callId) {
        const timelineEvent = TimelineEventService.createUnloadEvent(
          'unload_confirmed',
          callActive,
          {
            timestamp: new Date().toISOString()
          }
        );
        
        TimelineEventService.addTimelineEvent(callId, timelineEvent).catch(error => {
          Sentry.logger.error('Unload event storage exception', { error, component: 'useMonitoring', callId });
        });
      }
    };

    // Add event listeners
    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("unload", handleUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("unload", handleUnload);
    };
  }, [callActive, sendDataToRoom, callId, callType, isIntentionalEnding]);

  // Store monitoring control functions
  const monitoringControlRef = useRef<{
    stopMonitoring: (() => void) | null;
    isStarting: boolean;
  }>({
    stopMonitoring: null,
    isStarting: false
  });

  const startMonitorDetection = useCallback(async () => {
    if (!config.externalMonitorDetection || !room) {
      return;
    }
    if (monitoringControlRef.current.isStarting) {
      return;
    }
    
    // Starting monitor detection system
    
    monitoringControlRef.current.isStarting = true;
    
    try {
      await requestMonitorDetectionPermissions();
      // Significantly reduced frequency for LiveKit Cloud stability - prevents negotiation interference
      // Using debounced handler to prevent rapid successive calls
      const debouncedHandler = debounce(handleMonitorDetection, 500);
      monitoringControlRef.current.stopMonitoring = startMonitorMonitoring(debouncedHandler, 30000);
      
    } finally {
      monitoringControlRef.current.isStarting = false;
    }
  }, [config.externalMonitorDetection, room, handleMonitorDetection, requestMonitorDetectionPermissions, callActive, strictMonitorMode]);

  const stopMonitorDetection = useCallback(() => {
    if (monitoringControlRef.current.stopMonitoring) {
      monitoringControlRef.current.stopMonitoring();
      monitoringControlRef.current.stopMonitoring = null;
      // Monitor detection stopped
    }
  }, []);

  // External monitor detection effect with safe monitoring
  useEffect(() => {
    if (!config.externalMonitorDetection || !room) return;

    startMonitorDetection();
    
    return () => {
      stopMonitorDetection();
    };
  }, [config.externalMonitorDetection, room, startMonitorDetection, stopMonitorDetection]);

  // Monitor detection will only happen during requestPermissions() and active call monitoring
  // No initial check on page load

  // Permission status updates
  useEffect(() => {
    setMonitoringState(prev => ({
      ...prev,
      permissionStatus: {
        ...prev.permissionStatus,
        screenShare: config.screenSharingEnabled ? "pending" : "granted",
      },
    }));
  }, [config.screenSharingEnabled]);

  useEffect(() => {
    setMonitoringState(prev => ({
      ...prev,
      permissionStatus: {
        ...prev.permissionStatus,
        monitorCheck: config.externalMonitorDetection ? "pending" : "passed",
      },
    }));
  }, [config.externalMonitorDetection]);

  // Check for existing permissions on mount
  useEffect(() => {
    checkExistingPermissions();
  }, [checkExistingPermissions]);

  const clearMonitorWarning = useCallback(() => {
    setShowMonitorWarning(false);
  }, []);

  const clearMonitorBlock = useCallback(() => {
    setShowMonitorBlockDialog(false);
  }, []);

  const clearExternalMonitorWarning = useCallback(() => {
    setShowExternalMonitorWarning(false);
  }, []);

  const retryMonitorDetection = useCallback(async () => {
    if (!config.externalMonitorDetection) return true;

    // Reset monitor check status to pending
    setMonitoringState(prev => ({
      ...prev,
      permissionStatus: {
        ...prev.permissionStatus,
        monitorCheck: "pending",
      },
      monitorViolationDetected: false,
    }));

    // Clear any existing warnings/blocks
    setShowMonitorWarning(false);
    setShowMonitorBlockDialog(false);

    try {
      // Force a fresh monitor detection check
      const monitorResult = await checkForMultipleMonitors();
      const shouldBlock = monitorDetector.shouldBlockAccess(monitorResult, strictMonitorMode);

      if (shouldBlock) {
        handleMonitorDetection(monitorResult);
        return false;
      } else {
        setMonitoringState(prev => ({
          ...prev,
          permissionStatus: {
            ...prev.permissionStatus,
            monitorCheck: "passed",
          },
          currentMonitorResult: monitorResult,
          monitorViolationDetected: false,
        }));

        setShowExternalMonitorWarning(false);

        toast({
          title: "Monitor Check Passed",
          description: "Single display detected. You can now proceed.",
          variant: "success",
        });
        return true;
      }
    } catch (error) {
      Sentry.logger.error("Error during monitor detection retry", { error, component: 'useMonitoring' });
      setMonitoringState(prev => ({
        ...prev,
        permissionStatus: {
          ...prev.permissionStatus,
          monitorCheck: "failed",
        },
      }));
      return false;
    }
  }, [config.externalMonitorDetection, strictMonitorMode, handleMonitorDetection, toast]);

  const resetMonitoringStateForNewCall = useCallback(async () => {
    
    // Stop existing monitoring to prevent race conditions
    stopMonitorDetection();

    // Reset processing flags
    isProcessingMonitorDetectionRef.current = false;
    monitorViolationAlreadyDetectedRef.current = false;
    monitorChangeInProgressRef.current = false;

    // Reset global event suppression
    TimelineEventService.resetEventSuppression();
    
    // Use existing clear functions
    clearMonitorWarning();
    clearMonitorBlock();
    clearExternalMonitorWarning();
    
    // Reset violation counts
    setViolationCounts({
      tabChange: 0,
      windowBlur: 0,
      fullscreen: 0,
    });
    
    // Reset violation warning
    setShowViolationLimitWarning(false);
    
    // Reset tab change data
    setTabChangeData({
      count: 0,
      changes: [],
      lastVisibleTime: null,
    });

    // Get fresh monitor detection to establish new baseline
    if (config.externalMonitorDetection) {
      try {
        // Getting fresh monitor detection baseline
        const freshMonitorResult = await checkForMultipleMonitors();
        
        // CRITICAL: Only proceed if we detect a valid single-monitor state
        if (freshMonitorResult.hasMultipleMonitors || freshMonitorResult.screenCount > 1) {
          Sentry.logger.error("Cannot start call - multiple monitors still detected", { component: 'useMonitoring', freshMonitorResult });
          throw new Error("Multiple monitors detected during reset. Please ensure only one monitor is connected.");
        }
        
        setMonitoringState(prev => ({
          ...prev,
          // Set fresh baseline from current state
          currentMonitorResult: freshMonitorResult,
          monitorViolationDetected: false,
          currentScreenCount: freshMonitorResult.screenCount,
          previousScreenCount: freshMonitorResult.screenCount, // IMPORTANT: Set both to same value
          permissionStatus: {
            ...prev.permissionStatus,
            monitorCheck: freshMonitorResult.hasMultipleMonitors ? "failed" : "passed",
          },
        }));
        
        // Fresh monitor baseline established
        
        // Set flag to skip first violation check after reset
        skipNextViolationRef.current = true;
        
        // Wait a bit for state to settle, then restart monitoring
        setTimeout(() => {
          startMonitorDetection();
        }, 500);
      } catch (error) {
        Sentry.logger.error("Failed to get fresh monitor baseline", { error, component: 'useMonitoring' });
        throw error; // Re-throw to prevent call start if baseline check fails
      }
    } else {
      // If external monitor detection is disabled, just reset to clean state
      setMonitoringState(prev => ({
        ...prev,
        currentMonitorResult: null,
        monitorViolationDetected: false,
        currentScreenCount: 1,
        previousScreenCount: 1,
        permissionStatus: {
          ...prev.permissionStatus,
          monitorCheck: "passed",
        },
      }));
    }
    
    // Monitoring state reset completed
  }, [config.externalMonitorDetection, clearMonitorWarning, clearMonitorBlock, clearExternalMonitorWarning, checkForMultipleMonitors, stopMonitorDetection, startMonitorDetection]);

  // Handle fullscreen violations
  const handleFullscreenViolation = useCallback((type: 'fullscreen_exit' | 'fullscreen_escape_key', message: string, severity: 'warning' | 'penalty' | 'critical') => {

    // Store fullscreen violation event in database
    if (callId) {
      const timelineEvent = TimelineEventService.createFullscreenViolationEvent(
        type,
        message,
        severity,
        {
          timestamp: new Date().toISOString(),
          violationType: type
        }
      );
      
      TimelineEventService.addTimelineEvent(callId, timelineEvent)
        .then(() => {
          // Fullscreen violation event stored successfully
        })
        .catch(error => {
          Sentry.logger.error('Fullscreen violation event storage exception', { error, component: 'useMonitoring', callId });
        });
    }

    // Increment violation count
    setViolationCounts(prev => {
      const newFullscreenCount = prev.fullscreen + 1;
      const totalViolations = prev.tabChange + prev.windowBlur + newFullscreenCount;
      
      // Fullscreen violation count updated
      
      // Track violations but do not terminate call
      if (totalViolations >= VIOLATION_LIMIT) {
        AudioPlayer.playViolationSound();
        Sentry.logger.warn("Fullscreen violation limit reached but call continues", {
          component: 'useMonitoring',
          callId,
          totalViolations,
          newFullscreenCount,
          tabChangeCount: prev.tabChange,
          windowBlurCount: prev.windowBlur,
          violationLimit: VIOLATION_LIMIT
        });
      } else {
        // Always play normal violation sound since we don't terminate for fullscreen violations
        AudioPlayer.playViolationSound();
      }
      
      return { ...prev, fullscreen: newFullscreenCount };
    });

    // Add violation to the tracking system
    onAddViolation?.(type, message, severity);
    
    // Send data to room
    sendDataToRoom({
      type: "fullscreen_violation",
      action: type,
      timestamp: new Date().toISOString(),
      details: {
        message,
        severity,
        violationType: type
      }
    });
  }, [callActive, room?.state, callId, organizationId, onAddViolation, onEndCall, sendDataToRoom]);

  return {
    // State
    monitoringState,
    showMonitorWarning,
    showMonitorBlockDialog,
    showExternalMonitorWarning,
    tabChangeData,
    violationCounts,
    showViolationLimitWarning,
    validatedScreenStream: monitoringState.validatedScreenStream,

    // Actions
    requestPermissions,
    checkExistingPermissions,
    requestMonitorDetectionPermissions,
    handleMonitorDetection,
    handleFullscreenViolation,
    clearMonitorWarning,
    clearMonitorBlock,
    clearExternalMonitorWarning,
    retryMonitorDetection,
    resetMonitoringStateForNewCall,
    startMonitorDetection,
    stopMonitorDetection,

    // Utils
    sendDataToRoom,
  };
};
